/**
 * reportsService — My Reports data retrieval
 *
 * Architecture:
 * - Reports are searchable knowledge base of field data.
 * - 4 document types: Rock Tool Cards, Customer Site Visits, Site Audits, Rig Audits.
 * - Advanced filters: Sales area, Sales entity, Customer name, Site name, Date range.
 * - Row actions: copy, download, share (email only), view, favourite.
 * - Multi-select + download as ZIP.
 */
import { apiGet, apiPost } from './apiClient';

export const REPORT_TABS = [
  { key: 'rock-tool-cards', label: 'Rock tool cards', count: 12414 },
  { key: 'customer-site-visits', label: 'Customer site visits', count: 7095 },
  { key: 'site-audits', label: 'Site audits', count: 193 },
  { key: 'rig-audits', label: 'Rig audits', count: 202 },
];

export const REPORT_COLUMNS = {
  'rock-tool-cards': [
    { key: 'partNumber', label: 'Part number' },
    { key: 'description', label: 'Description' },
    { key: 'failureReason', label: 'Failure reason' },
    { key: 'serialNumber', label: 'Serial number' },
    { key: 'siteName', label: 'Site name' },
    { key: 'country', label: 'Country' },
    { key: 'customerName', label: 'Customer name' },
    { key: 'salesArea', label: 'Sales area' },
  ],
  'customer-site-visits': [
    { key: 'visitDate', label: 'Visit date' },
    { key: 'siteName', label: 'Site name' },
    { key: 'customerName', label: 'Customer name' },
    { key: 'country', label: 'Country' },
    { key: 'visitedBy', label: 'Visited by' },
    { key: 'summary', label: 'Summary' },
    { key: 'salesArea', label: 'Sales area' },
  ],
  'site-audits': [
    { key: 'auditDate', label: 'Audit date' },
    { key: 'siteName', label: 'Site name' },
    { key: 'customerName', label: 'Customer name' },
    { key: 'country', label: 'Country' },
    { key: 'auditType', label: 'Audit type' },
    { key: 'status', label: 'Status' },
    { key: 'salesArea', label: 'Sales area' },
  ],
  'rig-audits': [
    { key: 'auditDate', label: 'Audit date' },
    { key: 'rigModel', label: 'Rig model' },
    { key: 'siteName', label: 'Site name' },
    { key: 'customerName', label: 'Customer name' },
    { key: 'country', label: 'Country' },
    { key: 'status', label: 'Status' },
    { key: 'salesArea', label: 'Sales area' },
  ],
};

/**
 * Search reports
 * @param {Object} params - { tabKey, query, filters, page, pageSize }
 * @returns {Promise<Object>} { records: [...], totalCount, page }
 */
export async function searchReports(params = {}) {
  // TODO: Replace with real API call
  // return apiClient.get('/api/reports', { params });

  const { tabKey = 'rock-tool-cards' } = params;

  // Stub data for rock tool cards tab (matching screenshots)
  if (tabKey === 'rock-tool-cards') {
    return {
      records: [
        {
          id: 'rtc-001', partNumber: '0040284-15', description: '7 7/8" - X30 API L3',
          failureReason: 'Bearing and/or seal fail...', serialNumber: 'H81056975007',
          siteName: 'BARRICK Lu...', country: 'Zambia', customerName: 'LUMWANA ...',
          salesArea: 'CAFR-Cen...',
        },
        {
          id: 'rtc-002', partNumber: '0040284-15', description: '7 7/8" - X30 API L3',
          failureReason: 'Cone interference', serialNumber: 'H81066335014',
          siteName: 'BARRICK Lu...', country: 'Zambia', customerName: 'LUMWANA ...',
          salesArea: 'CAFR-Cen...',
        },
        {
          id: 'rtc-003', partNumber: '0040284-15', description: '7 7/8" - X30 API L3',
          failureReason: 'Cone interference', serialNumber: '20024K0610',
          siteName: 'BARRICK Lu...', country: 'Zambia', customerName: 'LUMWANA ...',
          salesArea: 'CAFR-Cen...',
        },
        {
          id: 'rtc-004', partNumber: '42-63ED165-...', description: 'BIT QL560-6 165 BD2/10 18S/...',
          failureReason: 'Excessive steel wear of ...', serialNumber: '241230890',
          siteName: 'Rocmin Caren.', country: 'Chile', customerName: 'ROCMIN SER...',
          salesArea: 'ANSCO-A...',
        },
        {
          id: 'rtc-005', partNumber: '42-88PE203-...', description: 'BIT M8 203 CC 3/9 18S/18S X...',
          failureReason: 'Top of button sheared o...', serialNumber: '250257303',
          siteName: 'BP - NPL PO...', country: 'Australia', customerName: 'NEW PORGE...',
          salesArea: 'ANZ-Aust...',
        },
        {
          id: 'rtc-006', partNumber: '42-88PE203-...', description: 'BIT M8 203 CC 3/9 18S/18S X...',
          failureReason: 'Button crushed inside b...', serialNumber: '250257142',
          siteName: 'BP - NPL PO...', country: 'Australia', customerName: 'NEW PORGE...',
          salesArea: 'ANZ-Aust...',
        },
        {
          id: 'rtc-007', partNumber: '42-485B127-...', description: 'BIT M40 CV 2/8 14S/14S SH69',
          failureReason: 'Button loose', serialNumber: '230150749',
          siteName: 'Antamina DTH', country: 'Peru', customerName: 'COMPANIA ...',
          salesArea: 'ANSCO-A...',
        },
        {
          id: 'rtc-008', partNumber: '42-485B127-...', description: 'BIT M40 127 CV 2/8 14S/14S ...',
          failureReason: 'Button crushed down to...', serialNumber: '221250962',
          siteName: 'Antamina DTH', country: 'Peru', customerName: 'COMPANIA ...',
          salesArea: 'ANSCO-A...',
        },
      ],
      totalCount: 12414,
      page: 1,
    };
  }

  // Stub for other tabs
  return { records: [], totalCount: REPORT_TABS.find(t => t.key === tabKey)?.count || 0, page: 1 };
}

/**
 * Get report download URL
 * @param {string} reportId
 * @returns {Promise<string>}
 */
export async function getReportDownloadUrl(reportId) {
  console.log('[reportsService] Download requested (stub):', reportId);
  return `/api/reports/${reportId}/download`;
}

/**
 * Download multiple reports as ZIP
 * @param {string[]} reportIds
 * @returns {Promise<string>} ZIP download URL
 */
export async function downloadReportsAsZip(reportIds) {
  console.log('[reportsService] ZIP download requested (stub):', reportIds);
  return `/api/reports/download-zip?ids=${reportIds.join(',')}`;
}

/**
 * Share report via email
 * @param {string} reportId
 * @param {Object} shareData - { recipientEmail, subject, message }
 * @returns {Promise<Object>}
 */
export async function shareReportByEmail(reportId, shareData) {
  // TODO: Replace with real API — sends email via configured Outlook/Gmail relay
  console.log('[reportsService] Email share requested (stub):', reportId, shareData);
  return { success: true, reportId };
}
